import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2, FiKey, FiUser } from 'react-icons/fi';
import './StudentsPage.css';

const StudentsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdminUser = user?.role === 'admin';
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: ''
    });

    useEffect(() => {
        loadStudents();
    }, []);

    const loadStudents = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getStudents();
            setStudents(response.data.students);
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке учеников');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (!isAdminUser) return;
            if (editingStudent) {
                await adminAPI.updateStudent(editingStudent.id, formData);
            } else {
                await adminAPI.createStudent(formData);
            }
            
            setShowModal(false);
            setEditingStudent(null);
            resetForm();
            loadStudents();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении ученика');
        }
    };

    const handleEdit = (student) => {
        setEditingStudent(student);
        setFormData({
            username: student.username,
            password: '',
            firstName: student.first_name,
            lastName: student.last_name || '',
            email: student.email || '',
            phone: student.phone || '',
            notes: student.notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!isAdminUser) return;
        if (!window.confirm('Вы уверены, что хотите удалить этого ученика?')) {
            return;
        }

        try {
            await adminAPI.deleteStudent(id);
            loadStudents();
        } catch (err) {
            setError('Ошибка при удалении ученика');
        }
    };

    const resetForm = () => {
        setFormData({
            username: '',
            password: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            notes: ''
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingStudent(null);
        resetForm();
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Ученики</h1>
                    <p>
                        {isAdminUser
                            ? 'Управление учениками и их аккаунтами'
                            : 'Ученики, закреплённые за вами'}
                    </p>
                </div>
                {isAdminUser && (
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                    >
                        <FiPlus /> Добавить ученика
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Имя</th>
                                <th>Логин</th>
                                <th>Email</th>
                                <th>Телефон</th>
                                <th>Последний вход</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center' }}>
                                        Нет учеников
                                    </td>
                                </tr>
                            ) : (
                                students.map((student) => (
                                    <tr key={student.id}>
                                        <td>{student.first_name} {student.last_name}</td>
                                        <td>{student.username}</td>
                                        <td>{student.email || '—'}</td>
                                        <td>{student.phone || '—'}</td>
                                        <td>
                                            {student.last_login 
                                                ? new Date(student.last_login).toLocaleDateString('ru-RU')
                                                : 'Не входил'
                                            }
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => navigate(`/admin/student/${student.id}`)}
                                                    title="Открыть профиль"
                                                >
                                                    <FiUser />
                                                </button>
                                                {isAdminUser && (
                                                    <>
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleEdit(student)}
                                                            title="Редактировать"
                                                        >
                                                            <FiEdit />
                                                        </button>
                                                        <button
                                                            className="btn-icon btn-danger"
                                                            onClick={() => handleDelete(student.id)}
                                                            title="Удалить"
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && isAdminUser && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingStudent ? 'Редактировать ученика' : 'Добавить ученика'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Имя *</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Фамилия</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Логин *</label>
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!!editingStudent}
                                    />
                                </div>

                                {!editingStudent && (
                                    <div className="form-group">
                                        <label>Пароль *</label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required={!editingStudent}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Телефон</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Заметки</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows="3"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingStudent ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentsPage;
